/**
 * ChatMessage — single message row.
 *
 * Handles the three flavors in one component (text, track-drop, deleted) plus
 * the admin/author affordances (pin, delete) and the reaction row. Rendering
 * the whole thing here keeps the parent room a pure list + scroll manager.
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { fileUrl, api, dollars } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlayer } from "@/context/PlayerContext";
import { Pin, Trash2, Flag, Music, Play } from "lucide-react";
import { toast } from "sonner";

const REACTIONS = ["🔥", "❤️", "🎧", "💯", "👀", "😂"];

// Turn @mentions into styled tokens without pulling in a full markdown lib.
// Keys embed the token's position + first chars so React can track them
// stably even if the message ever animates.
function renderText(text) {
  const parts = (text || "").split(/(@[a-zA-Z0-9_]{3,32})/g);
  return parts.map((p, i) => {
    const key = `${i}-${p.slice(0, 12)}`;
    if (p.startsWith("@")) {
      return <span key={key} className="text-[#FF4500] font-semibold">{p}</span>;
    }
    return <span key={key}>{p}</span>;
  });
}

function fmtTime(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const same = d.toDateString() === now.toDateString();
    return same
      ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
      : d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
}

export default function ChatMessage({ msg, onReport }) {
  const { user } = useAuth();
  const { playTrack } = usePlayer();
  const isAdmin = user?.role === "admin";
  const isOwn = user && user.id === msg.user_id;
  const isMention = user && Array.isArray(msg.mentions) && msg.mentions.includes(user.id);

  const roleBadge = useMemo(() => {
    if (msg.role === "admin") return { label: "FOUNDER", cls: "text-[#FF4500] bg-[#FF4500]/10 border-[#FF4500]/50" };
    if (msg.role === "artist") return { label: "ARTIST", cls: "text-white bg-white/10 border-white/30" };
    return null;
  }, [msg.role]);

  const react = async (emoji) => {
    try { await api.post(`/chat/messages/${msg.id}/react`, { emoji }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't react"); }
  };

  const del = async () => {
    if (!window.confirm("Delete this message?")) return;
    try { await api.delete(`/chat/messages/${msg.id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const pin = async () => {
    try { await api.post(`/chat/messages/${msg.id}/pin`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Pin failed"); }
  };

  if (msg.is_deleted) {
    return (
      <div data-testid={`chat-message-${msg.id}`} className="py-2 px-3 opacity-50">
        <div className="font-mono text-[11px] italic text-gray-500">— message removed —</div>
      </div>
    );
  }

  return (
    <div
      data-testid={`chat-message-${msg.id}`}
      className={`py-2 px-3 group ${isMention ? "bg-[#FF4500]/5 border-l-2 border-[#FF4500]" : "hover:bg-white/[0.02]"}`}
    >
      <div className="flex items-baseline gap-2 flex-wrap">
        <Link
          to={`/artist/${msg.user_id}`}
          data-testid={`chat-message-author-${msg.id}`}
          className="font-display text-sm text-white hover:text-[#FF4500]"
        >
          {msg.display_name || msg.username}
        </Link>
        {roleBadge && (
          <span className={`font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${roleBadge.cls}`}>
            {roleBadge.label}
          </span>
        )}
        <span className="font-mono text-[10px] text-gray-500">{fmtTime(msg.created_at)}</span>
        {msg.is_pinned && (
          <span className="font-mono text-[9px] uppercase tracking-widest text-[#FF4500] flex items-center gap-1">
            <Pin className="h-3 w-3" /> pinned
          </span>
        )}
        <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {!isOwn && (
            <button
              data-testid={`chat-report-${msg.id}`}
              onClick={() => onReport(msg)}
              title="Report"
              className="h-6 w-6 grid place-items-center text-gray-500 hover:text-yellow-500"
            >
              <Flag className="h-3 w-3" />
            </button>
          )}
          {isAdmin && (
            <button
              data-testid={`chat-pin-${msg.id}`}
              onClick={pin}
              title={msg.is_pinned ? "Unpin" : "Pin"}
              className="h-6 w-6 grid place-items-center text-gray-500 hover:text-[#FF4500]"
            >
              <Pin className="h-3 w-3" />
            </button>
          )}
          {(isAdmin || isOwn) && (
            <button
              data-testid={`chat-delete-${msg.id}`}
              onClick={del}
              title="Delete"
              className="h-6 w-6 grid place-items-center text-gray-500 hover:text-red-500"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      {msg.kind === "track_drop" && msg.track_snapshot ? (
        <TrackDropCard t={msg.track_snapshot} onPlay={() => playTrack({
          id: msg.track_snapshot.id,
          title: msg.track_snapshot.title,
          cover_path: msg.track_snapshot.cover_path,
          artist_id: msg.track_snapshot.artist_id,
          artist_name: msg.track_snapshot.artist_name,
          album_id: msg.track_snapshot.album_id,
          price_cents: msg.track_snapshot.price_cents,
          is_free: msg.track_snapshot.is_free,
        })} />
      ) : (
        msg.text && (
          <div data-testid={`chat-message-text-${msg.id}`} className="mt-0.5 text-sm text-gray-100 whitespace-pre-wrap break-words">
            {renderText(msg.text)}
          </div>
        )
      )}

      {/* Reactions */}
      <div className="mt-1 flex flex-wrap items-center gap-1">
        {Object.entries(msg.reactions || {}).map(([emoji, users]) => {
          const mine = user && users.includes(user.id);
          return (
            <button
              key={emoji}
              data-testid={`chat-reaction-${msg.id}-${emoji}`}
              onClick={() => react(emoji)}
              className={`h-6 px-2 border font-mono text-xs flex items-center gap-1 ${
                mine ? "border-[#FF4500] text-[#FF4500] bg-[#FF4500]/10" : "border-[#2A2A2A] text-gray-400 hover:border-white/40"
              }`}
            >
              <span>{emoji}</span>
              <span className="text-[10px]">{users.length}</span>
            </button>
          );
        })}
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5 ml-1">
          {REACTIONS.map((e) => (
            <button
              key={e}
              data-testid={`chat-react-add-${msg.id}-${e}`}
              onClick={() => react(e)}
              className="h-6 w-6 grid place-items-center border border-transparent hover:border-[#2A2A2A] text-sm"
              title={`React ${e}`}
            >
              {e}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function TrackDropCard({ t, onPlay }) {
  return (
    <div className="mt-2 border border-[#FF4500]/40 bg-[#FF4500]/[0.03] p-2 flex items-center gap-3 max-w-md">
      <div className="h-14 w-14 shrink-0 bg-neutral-800 overflow-hidden">
        {t.cover_path ? (
          <img src={fileUrl(t.cover_path)} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full grid place-items-center text-gray-600"><Music className="h-6 w-6" /></div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-display text-sm truncate">{t.title}</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 truncate">
          {t.artist_name} · {t.is_free ? "FREE" : dollars(t.price_cents || 99)}
        </div>
      </div>
      <button
        data-testid={`track-drop-play-${t.id}`}
        onClick={onPlay}
        className="h-9 w-9 grid place-items-center bg-[#FF4500] hover:bg-[#E53E00] text-black"
        title="Play preview"
      >
        <Play className="h-4 w-4" />
      </button>
    </div>
  );
}
